import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import {
  getProducto, getMovimientos, getUsoEnKits, getProveedores,
  actualizarProducto, editarProducto, borrarProducto, restaurarProducto,
  getKitsQueUsan, pausarProducto, ESTADO_STOCK,
  getKitsDisponibles, upsertProductoEnKit, quitarProductoDeKit,
  getGruposEquivalencia, getMiembrosGrupo, setGrupoEquivalencia
} from '../../lib/stockApi'
import { supabase } from '../../lib/supabase'

const CATEGORIAS = [
  { v: 'multiuso',   label: '🌟 Multiuso (limpiadores de superficies, todo uso)' },
  { v: 'lavarropa',  label: '🧺 Ropa (detergente, suavizante)' },
  { v: 'lavandinas', label: '🧪 Lavandinas (cloro / agua sanitaria)' },
  { v: 'cocina',     label: '🧴 Cocina (lavavajillas, desengrasante)' },
  { v: 'bano',       label: '🛁 Baño (limpiador específico, antical, desinfectante WC)' },
  { v: 'papel',      label: '🧻 Papel (higiénico, cocina, servilletas)' },
  { v: 'panuelos',   label: '🤧 Pañuelos descartables (packs de bolso, tissues)' },
  { v: 'higiene',    label: '🧼 Higiene (jabón manos, alcohol, gel)' },
  { v: 'bolsas',     label: '🛍️ Bolsas (residuos, freezer, supermercado)' },
  { v: 'accesorios', label: '🧽 Accesorios (esponjas, trapos, escobas)' },
  { v: 'otros',      label: '📦 Otros' },
]
const TIERS = [
  { v: 'A', label: 'A · Premium' },
  { v: 'B', label: 'B · Calidad/precio' },
  { v: 'C', label: 'C · Marca masiva' },
  { v: 'D', label: 'D · Especialidades' },
]

// Helpers IVA: convertir entre precio s/IVA (lo que se carga en el form) y c/IVA (lo que se guarda en DB).
function ivaFactor(pct) { return 1 + Number(pct || 22) / 100 }
function toSinIva(conIva, pct) {
  if (conIva == null || conIva === '') return ''
  return (Number(conIva) / ivaFactor(pct)).toFixed(2)
}
function toConIva(sinIva, pct) {
  if (sinIva == null || sinIva === '') return null
  return Number((Number(sinIva) * ivaFactor(pct)).toFixed(2))
}
function fmtMonto(n) {
  if (n == null || isNaN(n)) return '—'
  return '$' + Number(n).toLocaleString('es-UY', { maximumFractionDigits: 2 })
}

export default function Producto() {
  const { sku } = useParams()
  const navigate = useNavigate()
  const [producto, setProducto] = useState(null)
  const [movs, setMovs] = useState([])
  const [usoKits, setUsoKits] = useState([])
  const [proveedores, setProveedores] = useState([])
  const [demanda, setDemanda] = useState({ d7: null, d14: null, d30: null })
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editingFicha, setEditingFicha] = useState(false)
  const [pausarModal, setPausarModal] = useState(false)
  const [borrando, setBorrando] = useState(false)
  const [kitsDisponibles, setKitsDisponibles] = useState([])
  const [kitModal, setKitModal] = useState(null)  // null | { mode: 'add'|'edit', kitCodigo?, cantidad }
  const [gruposExistentes, setGruposExistentes] = useState([])
  const [miembrosGrupo, setMiembrosGrupo] = useState([])
  const [editandoGrupo, setEditandoGrupo] = useState(false)
  const [grupoInput, setGrupoInput] = useState('')

  useEffect(() => { load() }, [sku])

  async function load() {
    setLoading(true)
    const p = await getProducto(sku)
    setProducto(p)
    if (p) {
      const [m, k, prov, kits, grupos, miembros, d7, d14, d30] = await Promise.all([
        getMovimientos(p.id, 100),
        getUsoEnKits(p.id),
        getProveedores(),
        getKitsDisponibles(),
        getGruposEquivalencia(),
        p.grupo_equivalencia ? getMiembrosGrupo(p.grupo_equivalencia) : Promise.resolve([]),
        supabase.rpc('demanda_producto', { producto_id_param: p.id, dias_param: 7 }).then(r => r.data?.[0]),
        supabase.rpc('demanda_producto', { producto_id_param: p.id, dias_param: 14 }).then(r => r.data?.[0]),
        supabase.rpc('demanda_producto', { producto_id_param: p.id, dias_param: 30 }).then(r => r.data?.[0]),
      ])
      setMovs(m); setUsoKits(k); setProveedores(prov); setKitsDisponibles(kits)
      setGruposExistentes(grupos); setMiembrosGrupo(miembros)
      setGrupoInput(p.grupo_equivalencia || '')
      setDemanda({ d7, d14, d30 })
    }
    setLoading(false)
  }

  async function handleSaveGrupo() {
    if (!producto) return
    const valor = (grupoInput || '').trim().toLowerCase().replace(/\s+/g, '_')
    try {
      await setGrupoEquivalencia(producto.id, valor || null)
      setEditandoGrupo(false)
      await load()
    } catch (err) {
      alert('Error al guardar: ' + (err.message || err))
    }
  }

  async function handleQuitarGrupo() {
    if (!producto || !confirm('¿Sacar este producto del grupo de equivalencia?')) return
    try {
      await setGrupoEquivalencia(producto.id, null)
      setEditandoGrupo(false)
      await load()
    } catch (err) {
      alert('Error: ' + (err.message || err))
    }
  }

  async function handleSaveKitAssignment() {
    if (!kitModal || !producto) return
    const cantidad = Number(kitModal.cantidad)
    if (!kitModal.kitCodigo || !cantidad || cantidad <= 0) {
      alert('Elegí kit y cantidad mayor a 0')
      return
    }
    try {
      await upsertProductoEnKit(producto.id, kitModal.kitCodigo, cantidad)
      setKitModal(null)
      const k = await getUsoEnKits(producto.id)
      setUsoKits(k)
    } catch (err) {
      alert('Error al guardar: ' + (err.message || err))
    }
  }

  async function handleRemoveFromKit(kitCodigo, kitNombre) {
    if (!confirm(`¿Quitar este producto del kit "${kitNombre}"?`)) return
    try {
      await quitarProductoDeKit(producto.id, kitCodigo)
      const k = await getUsoEnKits(producto.id)
      setUsoKits(k)
    } catch (err) {
      alert('Error al quitar: ' + (err.message || err))
    }
  }

  // Evolución de costo: tomamos las últimas N entradas
  const evolucionCosto = useMemo(() => {
    return movs
      .filter(m => m.tipo === 'entrada' && m.costo_unitario)
      .reverse()  // cronológico
      .map(m => ({
        fecha: new Date(m.created_at).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit' }),
        costo: Number(m.costo_unitario),
        proveedor: m.proveedor?.nombre || '—'
      }))
  }, [movs])

  if (loading) return <AdminLayout><div className="loading">Cargando producto...</div></AdminLayout>
  if (!producto) return (
    <AdminLayout>
      <div className="page">
        <h1>Producto no encontrado</h1>
        <Link to="/admin/stock" className="btn ghost">← Volver a Stock</Link>
      </div>
    </AdminLayout>
  )

  const minMax = evolucionCosto.length > 1 ? {
    min: Math.min(...evolucionCosto.map(c => c.costo)),
    max: Math.max(...evolucionCosto.map(c => c.costo))
  } : null

  return (
    <AdminLayout>
      <div className="page">
        <Link to="/admin/stock" style={{ fontSize: 13, color: 'var(--muted)' }}>← Volver a stock</Link>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 8 }}>
          <div>
            <h1 style={{ marginBottom: 4 }}>{producto.nombre}</h1>
            <div style={{ color: 'var(--muted)', fontSize: 14 }}>
              <code style={{ background: 'var(--paper)', padding: '2px 8px', borderRadius: 4 }}>{producto.sku}</code>
              {producto.marca && <span> · {producto.marca}</span>}
              <span> · {producto.presentacion}</span>
              <span> · {producto.proveedor?.nombre || '— sin proveedor —'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {producto.deleted_at ? (
              <button
                className="btn primary"
                style={{ background: '#10B981', borderColor: '#10B981' }}
                onClick={async () => {
                  if (!confirm(`¿Restaurar ${producto.sku} — ${producto.nombre}?\nVolverá a aparecer en el listado y en la Tienda.`)) return
                  try { await restaurarProducto(producto.id); load() } catch (e) { alert(e.message) }
                }}
              >♻️ Restaurar</button>
            ) : producto.pausado ? (
              <button
                className="btn primary"
                style={{ background: '#10B981', borderColor: '#10B981' }}
                onClick={async () => {
                  if (!confirm('¿Reanudar este producto? Volverá a aparecer en la Tienda.')) return
                  try { await pausarProducto(producto.id, false); load() } catch (e) { alert(e.message) }
                }}
              >▶️ Reanudar</button>
            ) : (
              <button className="btn ghost" onClick={() => setPausarModal(true)} style={{ borderColor: '#F59E0B', color: '#92400E' }}>⏸️ Pausar</button>
            )}
            {!producto.deleted_at && !producto.pausado && (
              <button
                className="btn ghost"
                title={producto.publicado ? 'Visible en la tienda — click para ocultar' : 'Oculto en la tienda — click para publicar'}
                onClick={async () => {
                  try {
                    await actualizarProducto(producto.id, { publicado: !producto.publicado })
                    load()
                  } catch (e) { alert('Error: ' + e.message) }
                }}
                style={{
                  borderColor: producto.publicado ? '#10B981' : '#94A3B8',
                  color: producto.publicado ? '#065F46' : '#475569',
                  background: producto.publicado ? '#ECFDF5' : '#F1F5F9',
                  fontWeight: 700,
                }}
              >
                {producto.publicado ? '✅ En tienda' : '⬜ Oculto en tienda'}
              </button>
            )}
            <button className="btn ghost" onClick={() => setEditing(true)}>✏️ Editar producto</button>
            {!producto.deleted_at && (
              <button
                className="btn ghost"
                disabled={borrando}
                style={{ borderColor: '#EF4444', color: '#991B1B' }}
                onClick={async () => {
                  if (!confirm(
                    `¿Borrar ${producto.sku} — ${producto.nombre}?\n\n` +
                    `Si tiene compras, kits o movimientos previos, se marca como eliminado (podés restaurarlo).\n` +
                    `Si está limpio, se borra definitivamente.\n\n` +
                    `Esta acción no se puede deshacer en el caso de borrado físico.`
                  )) return
                  setBorrando(true)
                  try {
                    const res = await borrarProducto(producto.id)
                    if (res?.modo === 'hard_delete') {
                      alert(`✓ ${res.sku} borrado definitivamente.`)
                      navigate('/admin/stock')
                    } else {
                      alert(
                        `${res.sku} marcado como eliminado.\n` +
                        `Tiene ${res.movimientos} movimiento(s), ${res.kits} kit(s) y ${res.compras} compra(s) que impiden borrarlo del todo.\n` +
                        `Podés restaurarlo cuando quieras.`
                      )
                      load()
                    }
                  } catch (e) { alert('Error al borrar: ' + e.message) }
                  finally { setBorrando(false) }
                }}
              >🗑️ Borrar</button>
            )}
          </div>
        </div>

        {producto.deleted_at && (
          <div style={{
            marginTop: 14, padding: '12px 16px',
            background: '#FEE2E2', border: '2px solid #FCA5A5', borderRadius: 12,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>🗑️</span>
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#7F1D1D' }}>Producto eliminado</strong>
              <span style={{ color: '#991B1B' }}> — borrado el {new Date(producto.deleted_at).toLocaleDateString('es-UY')}</span>
              <div style={{ fontSize: 11, color: '#991B1B', marginTop: 2 }}>
                No aparece en el listado ni en la Tienda. Conserva el historial. Tocá <strong>♻️ Restaurar</strong> para revertirlo.
              </div>
            </div>
          </div>
        )}
        {!producto.deleted_at && producto.pausado && (
          <div style={{
            marginTop: 14, padding: '12px 16px',
            background: '#FEF3C7', border: '2px solid #FCD34D', borderRadius: 12,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>⏸️</span>
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#78350F' }}>Producto pausado</strong>
              {producto.pausado_motivo && <span style={{ color: '#92400E' }}> — {producto.pausado_motivo}</span>}
              <div style={{ fontSize: 11, color: '#92400E', marginTop: 2 }}>
                No aparece en la Tienda. Sigue en kits hasta reanudarlo o sustituirlo.
              </div>
            </div>
          </div>
        )}

        {/* Stats principales */}
        <div className="cards-grid">
          <div className="card">
            <h3>Stock actual</h3>
            <div className="stat" style={{ color: producto.stock_actual <= producto.stock_critico ? '#EF4444' : producto.stock_actual <= producto.stock_minimo ? '#F59E0B' : 'var(--navy-ink)' }}>
              {Number(producto.stock_actual).toLocaleString('es-UY')}
            </div>
            <div className="desc">Mín {producto.stock_minimo} · Crítico {producto.stock_critico}</div>
          </div>
          <div className="card">
            <h3>Costo unitario (PPP)</h3>
            <div className="stat">${Number(producto.costo_unitario).toFixed(2)}</div>
            <div className="desc">s/IVA · c/IVA ${(Number(producto.costo_unitario) * (1 + Number(producto.iva_pct ?? 22)/100)).toFixed(2)} ({producto.iva_pct ?? 22}%)</div>
          </div>
          <div className="card" style={{ borderLeft: '4px solid #C5A059' }}>
            <h3>Precio al público</h3>
            <div className="stat" style={{ color: '#C5A059' }}>
              {producto.precio_publico ? `$${Number(producto.precio_publico).toLocaleString('es-UY', { maximumFractionDigits: 0 })}` : '—'}
            </div>
            <div className="desc">{producto.precio_publico ? 'c/IVA · venta suelto' : 'Sin precio cargado'}</div>
          </div>
          {producto.precio_publico && Number(producto.precio_publico) > 0 && (() => {
            const ivaPct = Number(producto.iva_pct ?? 22)
            const costoCIva = Number(producto.costo_unitario) * (1 + ivaPct / 100)
            const margenAbs = Number(producto.precio_publico) - costoCIva
            const margenPct = (margenAbs / Number(producto.precio_publico)) * 100
            const color = margenPct > 50 ? '#059669' : margenPct > 30 ? '#D97706' : '#DC2626'
            return (
              <div className="card" style={{ borderLeft: `4px solid ${color}` }}>
                <h3>Margen unitario</h3>
                <div className="stat" style={{ color }}>{margenPct.toFixed(0)}%</div>
                <div className="desc">${margenAbs.toFixed(2)} por unidad · c/IVA {ivaPct}%</div>
              </div>
            )
          })()}
          <div className="card">
            <h3>Valor en stock</h3>
            <div className="stat">${(Number(producto.stock_actual) * Number(producto.costo_unitario)).toLocaleString('es-UY', { maximumFractionDigits: 0 })}</div>
            <div className="desc">s/IVA · al costo</div>
          </div>
          <div className="card" style={{ borderLeft: '4px solid var(--teal)' }}>
            <h3>Demanda 7 días</h3>
            <div className="stat" style={{ color: 'var(--teal)' }}>{Number(demanda.d7?.cantidad_total ?? 0).toFixed(0)}</div>
            <div className="desc">{demanda.d7?.cantidad_entregas ?? 0} entregas</div>
          </div>
          <div className="card">
            <h3>Demanda 30 días</h3>
            <div className="stat">{Number(demanda.d30?.cantidad_total ?? 0).toFixed(0)}</div>
            <div className="desc">{demanda.d30?.cantidad_entregas ?? 0} entregas</div>
          </div>
        </div>

        {/* Evolución de costo */}
        {/* FICHA DE TIENDA — descripción rica para el cliente */}
        <h2 className="section-head" style={{ marginTop: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>🛍️ Ficha de Tienda</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {(producto.imagen_url || producto.descripcion || producto.beneficios?.length > 0) ? (
              <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 999, background: '#DCFCE7', color: '#166534', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase' }}>✓ Completa</span>
            ) : (
              <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 999, background: '#FEF3C7', color: '#92400E', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase' }}>⚠️ Sin info</span>
            )}
            <button className="btn ghost" onClick={() => setEditingFicha(true)} style={{ fontSize: 12, padding: '6px 12px' }}>
              {producto.descripcion ? '✏️ Editar ficha' : '+ Cargar ficha'}
            </button>
          </div>
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 20, padding: 20, background: '#fff', border: '1px solid var(--line)', borderRadius: 12, marginBottom: 16 }}>
          <div style={{ background: 'var(--paper, #f8fafc)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180, padding: 14 }}>
            {producto.imagen_url
              ? <img src={producto.imagen_url} alt="" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain' }} />
              : <span style={{ fontSize: 56, opacity: .35 }}>📦</span>}
          </div>
          <div style={{ fontSize: 13 }}>
            {producto.descripcion ? (
              <p style={{ margin: '0 0 12px', color: 'var(--navy-ink)', lineHeight: 1.5 }}>{producto.descripcion}</p>
            ) : (
              <p style={{ margin: '0 0 12px', color: 'var(--muted)', fontStyle: 'italic' }}>Sin descripción cargada. El cliente solo ve nombre + marca + presentación.</p>
            )}
            {(producto.aroma || producto.rendimiento || producto.ideal_para) && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {producto.aroma && <span style={{ background: '#f1f5f9', padding: '3px 9px', borderRadius: 999, fontSize: 11 }}>🌸 {producto.aroma}</span>}
                {producto.rendimiento && <span style={{ background: '#f1f5f9', padding: '3px 9px', borderRadius: 999, fontSize: 11 }}>📐 {producto.rendimiento}</span>}
                {producto.ideal_para && <span style={{ background: '#f1f5f9', padding: '3px 9px', borderRadius: 999, fontSize: 11 }}>✓ {producto.ideal_para}</span>}
              </div>
            )}
            {producto.beneficios?.length > 0 && (
              <ul style={{ paddingLeft: 18, margin: '0 0 8px', color: 'var(--navy-ink)' }}>
                {producto.beneficios.map((b, i) => <li key={i} style={{ marginBottom: 2 }}>{b}</li>)}
              </ul>
            )}
            {producto.modo_uso && <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 6 }}><strong>Modo de uso:</strong> {producto.modo_uso}</div>}
            {producto.composicion && <div style={{ color: 'var(--muted)', fontSize: 12 }}><strong>Composición:</strong> {producto.composicion}</div>}
          </div>
        </div>

        <h2 className="section-head" style={{ marginTop: 32 }}>📈 Evolución de costo</h2>
        {evolucionCosto.length === 0 ? (
          <div className="card" style={{ color: 'var(--muted)' }}>Todavía no se cargó ninguna compra. Cuando registres compras vas a ver acá la curva de precios.</div>
        ) : (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 140, padding: '0 8px' }}>
              {evolucionCosto.map((c, i) => {
                const range = (minMax?.max ?? c.costo) - (minMax?.min ?? c.costo)
                const h = range > 0 ? ((c.costo - (minMax?.min ?? 0)) / range * 100 + 20) : 60
                const prevCosto = i > 0 ? evolucionCosto[i - 1].costo : c.costo
                const subio = c.costo > prevCosto
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: subio ? '#EF4444' : i > 0 ? '#10B981' : 'var(--navy-ink)' }}>
                      ${c.costo.toFixed(0)}
                    </div>
                    <div style={{ width: '100%', height: `${h}%`, background: subio ? '#FEE2E2' : i > 0 ? '#D1FAE5' : 'var(--teal-light)', borderRadius: '4px 4px 0 0', borderTop: `3px solid ${subio ? '#EF4444' : i > 0 ? '#10B981' : 'var(--teal)'}` }}></div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{c.fecha}</div>
                  </div>
                )
              })}
            </div>
            {minMax && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--line)' }}>
                <span>Mínimo histórico: <strong>${minMax.min.toFixed(2)}</strong></span>
                <span>Máximo histórico: <strong>${minMax.max.toFixed(2)}</strong></span>
                <span>Variación: <strong>{((minMax.max - minMax.min) / minMax.min * 100).toFixed(0)}%</strong></span>
              </div>
            )}
          </div>
        )}

        {/* Equivalencias */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 24 }}>
          <h2 className="section-head" style={{ margin: 0 }}>🔗 Equivalencias</h2>
          {!editandoGrupo && (
            <button
              onClick={() => { setGrupoInput(producto?.grupo_equivalencia || ''); setEditandoGrupo(true) }}
              style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >{producto?.grupo_equivalencia ? '✏️ Cambiar grupo' : '+ Asignar grupo'}</button>
          )}
        </div>

        {!editandoGrupo ? (
          producto?.grupo_equivalencia ? (
            <div className="card" style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{
                  background: '#F3E8FF', color: '#7C3AED',
                  padding: '4px 10px', borderRadius: 999,
                  fontSize: 12, fontWeight: 800,
                }}>🔗 {producto.grupo_equivalencia}</span>
                <button
                  onClick={handleQuitarGrupo}
                  style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#7F1D1D', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                >Sacar del grupo</button>
              </div>
              <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
                Este producto es intercambiable con los siguientes. Sus stocks se suman al calcular la lista de compra.
              </div>
              {miembrosGrupo.filter(m => m.id !== producto.id).length === 0 ? (
                <div style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>
                  Solo este producto está en el grupo. Asigná el mismo grupo a otros productos para que se sumen.
                </div>
              ) : (
                <table className="t-table" style={{ marginTop: 0 }}>
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Producto</th>
                      <th>Pres.</th>
                      <th style={{ textAlign: 'right' }}>Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {miembrosGrupo.filter(m => m.id !== producto.id).map(m => (
                      <tr key={m.id}>
                        <td><Link to={`/admin/stock/${m.sku}`} style={{ color: '#0F1F3D', fontWeight: 700, textDecoration: 'none' }}>{m.sku}</Link></td>
                        <td>{m.nombre}{m.marca && <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 6 }}>· {m.marca}</span>}</td>
                        <td style={{ fontSize: 12, color: '#64748b' }}>{m.presentacion || '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{Number(m.stock_actual || 0)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#F3E8FF', fontWeight: 800 }}>
                      <td colSpan={3} style={{ textAlign: 'right' }}>Stock combinado del grupo</td>
                      <td style={{ textAlign: 'right', color: '#7C3AED' }}>
                        {miembrosGrupo.reduce((s, m) => s + Number(m.stock_actual || 0), 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div className="card" style={{ color: 'var(--muted)', marginTop: 12 }}>
              Este producto no está agrupado. Si tenés otros con el mismo formato y función (ej: distintos aromas), asignáles el mismo grupo y la lista de compra los va a sumar.
            </div>
          )
        ) : (
          <div className="card" style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
              Grupo de equivalencia
            </label>
            <input
              type="text"
              list="grupos-list"
              value={grupoInput}
              onChange={e => setGrupoInput(e.target.value)}
              placeholder="ej: brilha_lavarropas_2l"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
            />
            <datalist id="grupos-list">
              {gruposExistentes.map(g => <option key={g} value={g} />)}
            </datalist>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              Usá el mismo nombre para todos los productos intercambiables. Se guarda en minúsculas, sin espacios.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                onClick={handleSaveGrupo}
                style={{ background: '#0F1F3D', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
              >Guardar</button>
              <button
                onClick={() => setEditandoGrupo(false)}
                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >Cancelar</button>
            </div>
          </div>
        )}

        {/* Uso en kits */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 24 }}>
          <h2 className="section-head" style={{ margin: 0 }}>📦 Se usa en {usoKits.length} kits</h2>
          <button
            className="btn"
            onClick={() => setKitModal({ mode: 'add', kitCodigo: '', cantidad: 1 })}
            style={{ background: '#0F1F3D', color: '#fff', padding: '8px 14px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            + Asignar a un kit
          </button>
        </div>
        {usoKits.length === 0 ? (
          <div className="card" style={{ color: 'var(--muted)', marginTop: 12 }}>Este producto no está en ningún kit. Tocá "Asignar a un kit" para agregarlo.</div>
        ) : (
          <table className="t-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Kit</th>
                <th>Tier</th>
                <th>Precio susc.</th>
                <th style={{ textAlign: 'right' }}>Cantidad</th>
                <th style={{ textAlign: 'right', width: 140 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usoKits.map(u => (
                <tr key={u.kit.codigo}>
                  <td><strong>{u.kit.nombre}</strong></td>
                  <td style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{u.kit.tier}</td>
                  <td>${Number(u.kit.precio_susc).toLocaleString('es-UY')}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>×{u.cantidad}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => setKitModal({ mode: 'edit', kitCodigo: u.kit.codigo, kitNombre: u.kit.nombre, cantidad: Number(u.cantidad) })}
                      title="Editar cantidad"
                      style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px', marginRight: 6, cursor: 'pointer', fontSize: 12 }}
                    >✏️ Editar</button>
                    <button
                      onClick={() => handleRemoveFromKit(u.kit.codigo, u.kit.nombre)}
                      title="Quitar del kit"
                      style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#7F1D1D', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}
                    >🗑️ Quitar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Modal: agregar / editar asignación a kit */}
        {kitModal && (
          <div
            onClick={() => setKitModal(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,31,61,.55)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 14, padding: 22, maxWidth: 440, width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,.25)'
              }}
            >
              <h3 style={{ margin: '0 0 4px', fontSize: 19, fontWeight: 800, color: '#0F1F3D' }}>
                {kitModal.mode === 'edit' ? `Editar cantidad en ${kitModal.kitNombre}` : 'Asignar a un kit'}
              </h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
                {kitModal.mode === 'edit'
                  ? `Cuántas unidades de ${producto?.nombre} lleva este kit.`
                  : `Elegí en qué kit va ${producto?.nombre} y cuántas unidades por kit.`}
              </p>

              {kitModal.mode === 'add' && (
                <label style={{ display: 'block', marginBottom: 14 }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>Kit</span>
                  <select
                    value={kitModal.kitCodigo}
                    onChange={e => setKitModal({ ...kitModal, kitCodigo: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
                  >
                    <option value="">— Elegí un kit —</option>
                    {kitsDisponibles
                      .filter(k => !usoKits.some(u => u.kit.codigo === k.codigo))
                      .map(k => (
                        <option key={k.codigo} value={k.codigo}>
                          {k.nombre} · {k.tier}
                        </option>
                      ))}
                  </select>
                  {kitsDisponibles.filter(k => !usoKits.some(u => u.kit.codigo === k.codigo)).length === 0 && (
                    <div style={{ fontSize: 12, color: '#92400E', marginTop: 6 }}>
                      Este producto ya está asignado a todos los kits.
                    </div>
                  )}
                </label>
              )}

              <label style={{ display: 'block', marginBottom: 16 }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>Cantidad por kit</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={kitModal.cantidad}
                  onChange={e => setKitModal({ ...kitModal, cantidad: e.target.value })}
                  autoFocus
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
                />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  En unidades reales del producto (botellas / rollos / unidades). Ej: si el kit lleva 1 L y la botella es 500 ml → poné 2.
                </div>
              </label>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setKitModal(null)}
                  style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveKitAssignment}
                  style={{ background: '#0F1F3D', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Movimientos */}
        <h2 className="section-head">🔄 Movimientos ({movs.length})</h2>
        {movs.length === 0 ? (
          <div className="card" style={{ color: 'var(--muted)' }}>Sin movimientos todavía.</div>
        ) : (
          <table className="t-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th style={{ textAlign: 'right' }}>Cantidad</th>
                <th style={{ textAlign: 'right' }}>Costo u.</th>
                <th>Proveedor / Entrega</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {movs.map(m => {
                const tipoColor = {
                  entrada: { bg: '#D1FAE5', color: '#065F46', sign: '+' },
                  salida: { bg: '#FEE2E2', color: '#991B1B', sign: '−' },
                  ajuste: { bg: '#FEF3C7', color: '#92400E', sign: '~' },
                  devolucion: { bg: '#E0F2FE', color: '#0C4A6E', sign: '↩' }
                }[m.tipo] || { bg: 'var(--paper)', color: 'var(--muted)', sign: '?' }
                return (
                  <tr key={m.id}>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>{new Date(m.created_at).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' })}</td>
                    <td>
                      <span style={{ background: tipoColor.bg, color: tipoColor.color, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                        {tipoColor.sign} {m.tipo}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: tipoColor.color }}>
                      {m.tipo === 'salida' || (m.tipo === 'ajuste' && m.cantidad < 0) ? '−' : '+'}{Math.abs(Number(m.cantidad))}
                    </td>
                    <td style={{ textAlign: 'right' }}>{m.costo_unitario ? `$${Number(m.costo_unitario).toFixed(2)}` : '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      {m.proveedor?.nombre && `📦 ${m.proveedor.nombre}`}
                      {m.entrega?.numero_orden && <code style={{ fontSize: 11, color: 'var(--muted)' }}>{m.entrega.numero_orden}</code>}
                      {m.referencia_externa && <div style={{ fontSize: 11, color: 'var(--muted)' }}>Fact: {m.referencia_externa}</div>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>{m.notas || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {editing && <EditModal producto={producto} proveedores={proveedores} onClose={() => setEditing(false)} onDone={(skuNuevo) => {
          setEditing(false)
          if (skuNuevo && skuNuevo !== producto.sku) navigate(`/admin/stock/${skuNuevo}`, { replace: true })
          else load()
        }} />}
        {editingFicha && <FichaTiendaModal producto={producto} proveedores={proveedores} onClose={() => setEditingFicha(false)} onDone={() => { setEditingFicha(false); load() }} />}
        {pausarModal && <PausarProductoModal producto={producto} onClose={() => setPausarModal(false)} onDone={() => { setPausarModal(false); load() }} />}
      </div>
    </AdminLayout>
  )
}

/* ─── Modal: pausar producto con warning de kits afectados ─── */
function PausarProductoModal({ producto, onClose, onDone }) {
  const [kits, setKits] = useState([])
  const [loading, setLoading] = useState(true)
  const [motivo, setMotivo] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    getKitsQueUsan(producto.id).then(k => { setKits(k); setLoading(false) })
  }, [producto.id])

  async function pausar() {
    setSaving(true); setErr(null)
    try {
      await pausarProducto(producto.id, true, motivo.trim() || null)
      onDone()
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  const totalClientes = kits.reduce((s, k) => s + Number(k.clientes_activos || 0), 0)

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 540, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: 6 }}>⏸️ Pausar producto</h2>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 18 }}>
          <code>{producto.sku}</code> · <strong>{producto.nombre}</strong>
        </p>

        {loading ? (
          <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 16 }}>Buscando kits afectados…</div>
        ) : kits.length > 0 ? (
          <div style={{ background: '#FEF2F2', border: '2px solid #FCA5A5', borderRadius: 12, padding: 14, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>⚠️</span>
              <div>
                <strong style={{ color: '#7F1D1D', fontSize: 14 }}>Atención: este producto está en {kits.length} kit{kits.length !== 1 ? 's' : ''}</strong>
                <div style={{ fontSize: 12, color: '#991B1B', marginTop: 4 }}>
                  Total <strong>{totalClientes}</strong> cliente{totalClientes !== 1 ? 's' : ''} suscripto{totalClientes !== 1 ? 's' : ''} reciben este producto cada mes. <strong>Tenés que conseguir un sustituto.</strong>
                </div>
              </div>
            </div>
            <ul style={{ paddingLeft: 20, margin: 0, fontSize: 13, color: '#7F1D1D' }}>
              {kits.map(k => (
                <li key={k.kit_codigo} style={{ marginBottom: 4 }}>
                  <strong>{k.kit_nombre}</strong> ({k.kit_tier}) — usa ×{k.cantidad}
                  {k.clientes_activos > 0 && <span> · <strong>{k.clientes_activos}</strong> clientes activos</span>}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: '#166534' }}>
            ✓ Este producto no está en ningún kit. Pausarlo solo lo saca de la Tienda.
          </div>
        )}

        <label style={{ display: 'block', marginBottom: 14 }}>
          <b style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>
            Motivo (opcional)
          </b>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2}
            placeholder="Ej: Drogería sin stock hasta el lunes 6/5" style={{ width: '100%' }} />
        </label>

        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn primary" onClick={pausar} disabled={saving}
            style={{ background: '#F59E0B', borderColor: '#F59E0B' }}>
            {saving ? 'Pausando…' : '⏸️ Confirmar pausa'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── Modal para editar la Ficha de Tienda ─── */
function FichaTiendaModal({ producto, proveedores = [], onClose, onDone }) {
  const [form, setForm] = useState({
    descripcion: producto.descripcion || '',
    aroma:       producto.aroma || '',
    rendimiento: producto.rendimiento || '',
    beneficios:  Array.isArray(producto.beneficios) ? producto.beneficios.join('\n') : '',
    modo_uso:    producto.modo_uso || '',
    ideal_para:  producto.ideal_para || '',
    composicion: producto.composicion || '',
    proveedor_id: producto.proveedor?.id || producto.proveedor_id || '',
  })
  const [imagenUrl, setImagenUrl] = useState(producto.imagen_url || '')
  const [subiendo, setSubiendo] = useState(false)
  const [modoActivo, setModoActivo] = useState(null) // 'imagen' | 'texto' | 'todo'
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  // Refs para los inputs ocultos (para que cada botón abra su propio file picker)
  const fileInputRefs = {
    imagen: useRef(null),
    texto: useRef(null),
    todo: useRef(null),
  }

  function pickFile(modo) {
    setErr(null)
    fileInputRefs[modo].current?.click()
  }

  // Reduce foto a max 1280px + JPEG 0.88 antes de subir.
  // Evita WORKER_LIMIT_EXCEEDED por imágenes pesadas del iPad (5-10 MB).
  async function reducirEnNavegador(file, maxLado = 1280, calidad = 0.88) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onerror = () => reject(new Error('No se pudo leer la foto'))
      reader.onload = () => {
        const img = new Image()
        img.onerror = () => reject(new Error('Foto inválida o corrupta'))
        img.onload = () => {
          let { width: w, height: h } = img
          if (w > maxLado || h > maxLado) {
            const ratio = maxLado / Math.max(w, h)
            w = Math.round(w * ratio); h = Math.round(h * ratio)
          }
          const canvas = document.createElement('canvas')
          canvas.width = w; canvas.height = h
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, w, h)
          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error('No se pudo comprimir'))
              const reduced = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })
              resolve(reduced)
            },
            'image/jpeg', calidad
          )
        }
        img.src = reader.result
      }
      reader.readAsDataURL(file)
    })
  }

  async function handleFoto(e, modo) {
    const fileOriginal = e.target.files?.[0]
    if (!fileOriginal) return
    setSubiendo(true); setModoActivo(modo); setErr(null)
    try {
      // Reducir foto en navegador si es > 800 KB
      let file = fileOriginal
      if (fileOriginal.size > 800 * 1024) {
        try {
          file = await reducirEnNavegador(fileOriginal, 1280, 0.88)
          console.log(`[foto] Reducida: ${(fileOriginal.size/1024).toFixed(0)}KB → ${(file.size/1024).toFixed(0)}KB`)
        } catch (e) {
          console.warn('[foto] No se pudo reducir, subiendo original:', e.message)
          file = fileOriginal
        }
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('sku', producto.sku)
      formData.append('modo', modo)  // 'imagen' | 'texto' | 'todo'

      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/procesar-foto-producto`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: formData,
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`)
      if (data?.error) throw new Error(data.error)

      // Log detallado para debug
      console.log('[procesar-foto] respuesta:', {
        modo,
        flux_aplicado: data.flux_aplicado,
        flux_error: data.flux_error,
        remove_bg_aplicado: data.remove_bg_aplicado,
        campos_actualizados: data.campos_actualizados,
        imagen_url: data.imagen_url,
      })
      if ((modo === 'imagen' || modo === 'todo') && !data.flux_aplicado) {
        const errMsg = data.flux_error || 'Flux no se aplicó (REPLICATE_API_TOKEN faltante?)'
        console.warn('⚠️ Flux NO aplicó:', errMsg)
        setErr(`Foto subida pero sin escena premium. Razón: ${errMsg}`)
      }

      // Si procesó imagen, actualizar preview
      if (modo === 'imagen' || modo === 'todo') {
        setImagenUrl(data.imagen_url)
      }
      // Si analizó texto, actualizar form (solo campos vacíos en modo 'todo', todos en modo 'texto')
      if (modo === 'texto' || modo === 'todo') {
        const ficha = data.ficha || {}
        setForm(f => ({
          ...f,
          // En modo 'texto' siempre pisamos. En 'todo' solo si vacío.
          descripcion: (modo === 'texto' || !f.descripcion) ? (ficha.descripcion || f.descripcion) : f.descripcion,
          aroma:       (modo === 'texto' || !f.aroma)       ? (ficha.aroma       || f.aroma)       : f.aroma,
          rendimiento: (modo === 'texto' || !f.rendimiento) ? (ficha.rendimiento || f.rendimiento) : f.rendimiento,
          beneficios:  (modo === 'texto' || !f.beneficios)
            ? (Array.isArray(ficha.beneficios) && ficha.beneficios.length > 0 ? ficha.beneficios.join('\n') : f.beneficios)
            : f.beneficios,
          modo_uso:    (modo === 'texto' || !f.modo_uso)    ? (ficha.modo_uso    || f.modo_uso)    : f.modo_uso,
          ideal_para:  (modo === 'texto' || !f.ideal_para)  ? (ficha.ideal_para  || f.ideal_para)  : f.ideal_para,
          composicion: (modo === 'texto' || !f.composicion) ? (ficha.composicion || f.composicion) : f.composicion,
        }))
      }
    } catch (e) {
      setErr('Error procesando: ' + (e.message || String(e)))
    } finally {
      setSubiendo(false); setModoActivo(null)
      // Resetear input para permitir re-subir mismo archivo
      if (fileInputRefs[modo].current) fileInputRefs[modo].current.value = ''
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null); setSaving(true)
    try {
      const beneficios = form.beneficios.split('\n').map(s => s.trim()).filter(Boolean)
      // RPC distingue: '' = borrar el campo, null = mantener anterior.
      // Mandamos siempre el valor del form (incluyendo '' si el usuario lo vació).
      const { error } = await supabase.rpc('admin_actualizar_ficha_producto', {
        p_id: producto.id,
        p_imagen_url: imagenUrl ?? '',
        p_descripcion: form.descripcion ?? '',
        p_aroma: form.aroma ?? '',
        p_rendimiento: form.rendimiento ?? '',
        p_beneficios: beneficios.length > 0 ? beneficios : [],
        p_modo_uso: form.modo_uso ?? '',
        p_ideal_para: form.ideal_para ?? '',
        p_composicion: form.composicion ?? '',
      })
      if (error) throw error
      // Actualizar proveedor si cambió (la RPC de ficha no lo maneja)
      if ((producto.proveedor?.id || producto.proveedor_id || '') !== (form.proveedor_id || '')) {
        await actualizarProducto(producto.id, { proveedor_id: form.proveedor_id || null })
      }
      onDone()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 640, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: 6 }}>🛍️ Ficha de Tienda</h2>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 18 }}>
          Esta info se ve en el modal de detalle del producto cuando el cliente clickea en la Tienda.
        </p>

        <form onSubmit={handleSubmit}>
          <Field label="Foto del producto">
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 14, alignItems: 'start' }}>
              <div style={{ background: 'var(--paper, #f8fafc)', borderRadius: 10, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {imagenUrl
                  ? <img src={imagenUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <span style={{ fontSize: 36, opacity: .35 }}>📦</span>}
              </div>
              <div>
                {/* Inputs ocultos — uno por cada modo */}
                <input ref={fileInputRefs.imagen} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFoto(e, 'imagen')} style={{ display: 'none' }} />
                <input ref={fileInputRefs.texto}  type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFoto(e, 'texto')}  style={{ display: 'none' }} />
                <input ref={fileInputRefs.todo}   type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFoto(e, 'todo')}   style={{ display: 'none' }} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button type="button" onClick={() => pickFile('imagen')} disabled={subiendo}
                    style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid #00C4D4', background: '#fff', color: '#0F1F3D', cursor: subiendo ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
                    {subiendo && modoActivo === 'imagen' ? '🎨 Generando escena…' : '🖼️ Cambiar foto (mantiene texto)'}
                  </button>
                  <button type="button" onClick={() => pickFile('texto')} disabled={subiendo}
                    style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid #1A5CB8', background: '#fff', color: '#0F1F3D', cursor: subiendo ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
                    {subiendo && modoActivo === 'texto' ? '📋 Leyendo etiqueta…' : '📋 Leer etiqueta y autocompletar texto'}
                  </button>
                  <button type="button" onClick={() => pickFile('todo')} disabled={subiendo}
                    style={{ padding: '8px 12px', fontSize: 12, fontWeight: 700, borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #00C4D4 0%, #1A5CB8 100%)', color: '#fff', cursor: subiendo ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
                    {subiendo && modoActivo === 'todo' ? '✨ Procesando todo…' : '✨ Procesar todo (foto + texto)'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, lineHeight: 1.4 }}>
                  {subiendo
                    ? '🤖 Subiendo y procesando con IA… (15-50 seg)'
                    : <>
                        🖼️ <strong>Cambiar foto</strong>: regenera escena premium · NO toca texto<br/>
                        📋 <strong>Leer etiqueta</strong>: extrae info de la foto · NO toca imagen<br/>
                        ✨ <strong>Procesar todo</strong>: producto nuevo desde cero
                      </>}
                </div>
              </div>
            </div>
          </Field>

          <Field label="Proveedor">
            <select value={form.proveedor_id} onChange={e => setForm({...form, proveedor_id: e.target.value})}>
              <option value="">— sin proveedor —</option>
              {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </Field>

          <Field label="Descripción corta">
            <textarea value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})}
              rows="2" placeholder="Ej: Suavizante premium con perfume duradero. Su fórmula concentrada rinde hasta 30 lavados..." />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Aroma / Variante">
              <input type="text" value={form.aroma} onChange={e => setForm({...form, aroma: e.target.value})}
                placeholder="Ej: Blue Soft (floral azul)" />
            </Field>
            <Field label="Rendimiento">
              <input type="text" value={form.rendimiento} onChange={e => setForm({...form, rendimiento: e.target.value})}
                placeholder="Ej: Hasta 30 lavados" />
            </Field>
          </div>

          <Field label="Beneficios (uno por línea — los muestra como ✓ en la ficha)">
            <textarea value={form.beneficios} onChange={e => setForm({...form, beneficios: e.target.value})}
              rows="4" placeholder={'Acción anti pelusas\n4× más perfume\nBase 100% vegetal\nEnvase reciclable'} />
          </Field>

          <Field label="Ideal para">
            <input type="text" value={form.ideal_para} onChange={e => setForm({...form, ideal_para: e.target.value})}
              placeholder="Ej: Ropa de color y delicada" />
          </Field>

          <Field label="Modo de uso">
            <textarea value={form.modo_uso} onChange={e => setForm({...form, modo_uso: e.target.value})}
              rows="2" placeholder="Ej: Agregar 30 ml al cajón del suavizante por carga." />
          </Field>

          <Field label="Composición">
            <input type="text" value={form.composicion} onChange={e => setForm({...form, composicion: e.target.value})}
              placeholder="Ej: Base 100% vegetal · Sin fosfatos" />
          </Field>

          {err && <div className="err-box">{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn primary" disabled={saving || subiendo}>
              {saving ? 'Guardando...' : 'Guardar ficha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EditModal({ producto, proveedores, onClose, onDone }) {
  const etiquetasIniciales = Array.isArray(producto.etiquetas_urls) ? producto.etiquetas_urls : []
  const ivaInicial = producto.iva_pct ?? 22
  const [form, setForm] = useState({
    sku:            producto.sku,
    nombre:         producto.nombre,
    marca:          producto.marca || '',
    presentacion:   producto.presentacion || '',
    categoria:      producto.categoria || '',
    tier:           producto.tier || 'C',
    iva_pct:        ivaInicial,
    // Precios cargados como s/IVA (calculados a partir de los c/IVA guardados en DB).
    costo_unitario: toSinIva(producto.costo_unitario, ivaInicial),
    precio_publico: toSinIva(producto.precio_publico, ivaInicial),
    precio_mercado: toSinIva(producto.precio_mercado, ivaInicial),
    stock_minimo:   producto.stock_minimo ?? 0,
    stock_critico:  producto.stock_critico ?? 0,
    proveedor_id:   producto.proveedor?.id || producto.proveedor_id || '',
    vendible_suelto: producto.vendible_suelto !== false,
    notas:          producto.notas || '',
  })
  const [etiquetasExistentes, setEtiquetasExistentes] = useState(etiquetasIniciales)
  // Nuevas etiquetas a sumar (cada item: { file: File, preview: string })
  const [nuevasEtiquetas, setNuevasEtiquetas] = useState([])
  const [reanalizando, setReanalizando] = useState(false)
  const [fichaIA, setFichaIA] = useState(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  function handleNuevasEtiquetas(e) {
    const nuevos = Array.from(e.target.files || [])
    if (!nuevos.length) return
    setNuevasEtiquetas(prev => [
      ...prev,
      ...nuevos.map(file => ({ file, preview: URL.createObjectURL(file) })),
    ])
    e.target.value = ''
  }
  function quitarNueva(idx) {
    setNuevasEtiquetas(prev => {
      const it = prev[idx]
      if (it?.preview) URL.revokeObjectURL(it.preview)
      return prev.filter((_, i) => i !== idx)
    })
  }
  function quitarExistente(idx) {
    if (!confirm('¿Quitar esta foto de etiqueta? La URL se va a desvincular del producto al guardar.')) return
    setEtiquetasExistentes(prev => prev.filter((_, i) => i !== idx))
  }

  // Re-analizar con IA usando la imagen principal + las nuevas etiquetas (no se guarda nada hasta apretar "Guardar").
  async function reanalizarIA() {
    if (!producto.imagen_url && nuevasEtiquetas.length === 0) {
      setErr('Necesitás imagen del producto o al menos una etiqueta nueva para re-analizar.')
      return
    }
    setReanalizando(true); setErr(null)
    try {
      const fd = new FormData()
      // Si no hay foto local, usamos la URL existente bajándola.
      let mainFile = null
      if (producto.imagen_url) {
        const r = await fetch(producto.imagen_url)
        const blob = await r.blob()
        mainFile = new File([blob], `${producto.sku}.jpg`, { type: blob.type || 'image/jpeg' })
      }
      if (!mainFile && nuevasEtiquetas.length > 0) {
        mainFile = nuevasEtiquetas[0].file
      }
      if (!mainFile) throw new Error('Sin imagen para analizar')
      fd.append('file', mainFile)
      nuevasEtiquetas.forEach((it, i) => fd.append(`file_etiqueta_${i}`, it.file))

      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analizar-foto-producto`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: fd,
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`)
      const f = data.ficha || {}
      setFichaIA(f)
      // Solo prellena campos que están vacíos para no pisar correcciones manuales.
      setForm(prev => ({
        ...prev,
        nombre:       prev.nombre || f.nombre || prev.nombre,
        marca:        prev.marca || f.marca || prev.marca,
        presentacion: prev.presentacion || f.presentacion || prev.presentacion,
        categoria:    prev.categoria || f.categoria || prev.categoria,
        tier:         prev.tier || f.tier || prev.tier,
      }))
    } catch (e) {
      setErr('No se pudo re-analizar: ' + e.message)
    } finally { setReanalizando(false) }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!form.sku.trim()) { setErr('Falta SKU'); return }
    if (!form.nombre.trim()) { setErr('Falta nombre'); return }
    if (!form.categoria) { setErr('Falta categoría'); return }
    setSaving(true)
    try {
      // 1) Editar datos básicos (incluye validación de duplicado de SKU server-side).
      // Precios: el form los tiene s/IVA, los convertimos a c/IVA usando form.iva_pct para guardar en DB.
      await editarProducto(producto.id, {
        sku: form.sku.trim().toUpperCase(),
        nombre: form.nombre.trim(),
        categoria: form.categoria,
        tier: form.tier,
        marca: form.marca.trim() || null,
        presentacion: form.presentacion.trim() || null,
        iva_pct: Number(form.iva_pct) || 22,
        costo_unitario: toConIva(form.costo_unitario, form.iva_pct),
        precio_publico: toConIva(form.precio_publico, form.iva_pct),
        precio_mercado: toConIva(form.precio_mercado, form.iva_pct),
        stock_minimo: form.stock_minimo === '' ? null : parseFloat(form.stock_minimo),
        stock_critico: form.stock_critico === '' ? null : parseFloat(form.stock_critico),
        proveedor_id: form.proveedor_id || null,
        vendible_suelto: form.vendible_suelto,
        notas: form.notas.trim() || null,
      })

      const skuNuevo = form.sku.trim().toUpperCase()

      // 2) Si quitaron etiquetas existentes, persistir el array recortado.
      if (etiquetasExistentes.length !== etiquetasIniciales.length) {
        await actualizarProducto(producto.id, { etiquetas_urls: etiquetasExistentes })
      }

      // 3) Si hay nuevas etiquetas, las subimos via procesar-foto-producto y se concatenan al array.
      if (nuevasEtiquetas.length > 0) {
        try {
          const fd = new FormData()
          // Necesita una foto principal: si tenemos imagen_url la bajamos; si no, la primera nueva.
          let mainFile = null
          if (producto.imagen_url) {
            const r = await fetch(producto.imagen_url)
            const blob = await r.blob()
            mainFile = new File([blob], `${skuNuevo}.jpg`, { type: blob.type || 'image/jpeg' })
          } else {
            mainFile = nuevasEtiquetas[0].file
          }
          fd.append('file', mainFile)
          fd.append('sku', skuNuevo)
          nuevasEtiquetas.forEach((it, i) => fd.append(`file_etiqueta_${i}`, it.file))
          if (fichaIA) fd.append('ficha', JSON.stringify(fichaIA))
          // Para preservar las etiquetas que ya tenía (procesar-foto-producto reemplaza el array),
          // mandamos el array previo concatenado en una columna aparte y lo re-aplicamos después.
          const { data: { session } } = await supabase.auth.getSession()
          const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/procesar-foto-producto`
          const resp = await fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session?.access_token || ''}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: fd,
          })
          const data = await resp.json().catch(() => ({}))
          if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`)
          // Mergear: las nuevas que devuelve la function + las que el usuario quiso conservar.
          const subidas = Array.isArray(data?.etiquetas_urls) ? data.etiquetas_urls : []
          const merged = [...etiquetasExistentes, ...subidas]
          if (merged.length > 0) {
            await actualizarProducto(producto.id, { etiquetas_urls: merged })
          }
        } catch (e) {
          alert('Datos guardados, pero no se pudieron subir las nuevas etiquetas: ' + e.message)
        }
      }

      onDone(skuNuevo)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  const skuCambia = form.sku.trim().toUpperCase() !== producto.sku

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 640, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: 6 }}>✏️ Editar producto</h2>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 18 }}>
          Editás todos los datos básicos. Si subís nuevas fotos de etiqueta, podés re-analizar con IA antes de guardar.
        </p>

        <form onSubmit={handleSubmit}>
          {/* Bloque etiquetas: existentes + nuevas */}
          <div style={{ background: '#F8F6F1', border: '2px dashed #00A8B5', borderRadius: 12, padding: 16, marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
              <strong style={{ fontSize: 13, color: '#1A3461' }}>🏷️ Fotos de etiqueta</strong>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                {etiquetasExistentes.length} guardadas · {nuevasEtiquetas.length} nuevas
              </span>
            </div>

            {(etiquetasExistentes.length > 0 || nuevasEtiquetas.length > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 6, marginBottom: 10 }}>
                {etiquetasExistentes.map((url, i) => (
                  <div key={`ex-${i}`} style={{ position: 'relative', background: '#fff', borderRadius: 8, height: 100, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                    <img src={url} alt={`Etiqueta ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button type="button" onClick={() => quitarExistente(i)} title="Quitar"
                      style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: 'rgba(26,26,26,.85)', color: '#fff', border: 0, cursor: 'pointer', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>×</button>
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(26,52,97,.85)', color: '#fff', fontSize: 10, padding: '2px 6px', fontWeight: 700 }}>guardada</div>
                  </div>
                ))}
                {nuevasEtiquetas.map((it, i) => (
                  <div key={`new-${i}`} style={{ position: 'relative', background: '#fff', borderRadius: 8, height: 100, overflow: 'hidden', border: '2px solid #00A8B5' }}>
                    <img src={it.preview} alt={`Nueva ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button type="button" onClick={() => quitarNueva(i)} title="Quitar"
                      style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: 'rgba(26,26,26,.85)', color: '#fff', border: 0, cursor: 'pointer', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>×</button>
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#00A8B5', color: '#fff', fontSize: 10, padding: '2px 6px', fontWeight: 700 }}>nueva</div>
                  </div>
                ))}
              </div>
            )}

            <input type="file" accept="image/jpeg,image/png,image/webp" multiple
              onChange={handleNuevasEtiquetas} disabled={reanalizando || saving}
              style={{ width: '100%', fontSize: 11 }} />

            {nuevasEtiquetas.length > 0 && (
              <button type="button" className="btn ghost" onClick={reanalizarIA}
                disabled={reanalizando || saving}
                style={{ marginTop: 10, width: '100%', borderColor: '#1A3461', color: '#1A3461' }}>
                {reanalizando ? '🤖 Re-analizando con IA…' : '✨ Re-analizar con IA (foto + nuevas etiquetas)'}
              </button>
            )}
            {fichaIA && (
              <div style={{ fontSize: 11, color: '#00A8B5', marginTop: 6, fontWeight: 700, textAlign: 'center' }}>
                ✓ Datos detectados — campos vacíos prellenados, los completos quedaron como estaban.
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <Field label="SKU" required>
              <input type="text" required value={form.sku}
                onChange={e => setForm({...form, sku: e.target.value.toUpperCase()})}
                maxLength={20} style={{ fontFamily: 'monospace' }} />
              {skuCambia && (
                <div style={{ fontSize: 11, color: '#92400E', marginTop: 4 }}>
                  ⚠️ Cambiás SKU de <code>{producto.sku}</code> → <code>{form.sku.trim().toUpperCase()}</code>. Se valida que no haya otro igual.
                </div>
              )}
            </Field>
            <Field label="Nombre" required>
              <input type="text" required value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Marca">
              <input type="text" value={form.marca} onChange={e => setForm({...form, marca: e.target.value})} />
            </Field>
            <Field label="Presentación">
              <input type="text" value={form.presentacion} onChange={e => setForm({...form, presentacion: e.target.value})} placeholder="1 L · 500 mL · 12 unid" />
            </Field>
          </div>

          <Field label="Categoría" required>
            <select required value={form.categoria} onChange={e => setForm({...form, categoria: e.target.value})}>
              <option value="">— elegir —</option>
              {CATEGORIAS.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Tier">
            <select value={form.tier} onChange={e => setForm({...form, tier: e.target.value})}>
              {TIERS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>

          <h3 className="section-head" style={{ marginTop: 18 }}>💰 Precios</h3>
          <div style={{ background: '#f0fcfd', border: '1px solid #b3eef3', padding: '8px 12px', borderRadius: 8, fontSize: 12, color: 'var(--navy-ink)', marginBottom: 10 }}>
            💡 Cargá los precios <b>sin IVA</b>. Definí el IVA del producto abajo. El sistema calcula el precio con IVA automáticamente.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
            <Field label="IVA del producto">
              <select value={form.iva_pct} onChange={e => setForm({...form, iva_pct: Number(e.target.value)})}>
                <option value={22}>22% · General</option>
                <option value={10}>10% · Canasta básica</option>
              </select>
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Costo unitario s/IVA (UYU)">
              <input type="number" min="0" step="0.01" value={form.costo_unitario} onChange={e => setForm({...form, costo_unitario: e.target.value.replace(',', '.')})} />
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>= {fmtMonto(toConIva(form.costo_unitario, form.iva_pct))} c/IVA {form.iva_pct}%</div>
            </Field>
            <Field label="Precio público s/IVA (UYU)">
              <input type="number" min="0" step="0.01" value={form.precio_publico} onChange={e => setForm({...form, precio_publico: e.target.value.replace(',', '.')})} />
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>= {fmtMonto(toConIva(form.precio_publico, form.iva_pct))} c/IVA {form.iva_pct}%</div>
            </Field>
            <Field label="Precio mercado s/IVA (UYU)">
              <input type="number" min="0" step="0.01" value={form.precio_mercado} onChange={e => setForm({...form, precio_mercado: e.target.value.replace(',', '.')})} />
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>= {fmtMonto(toConIva(form.precio_mercado, form.iva_pct))} c/IVA {form.iva_pct}%</div>
            </Field>
          </div>

          <h3 className="section-head">📦 Stock</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Stock mínimo">
              <input type="number" min="0" step="1" value={form.stock_minimo} onChange={e => setForm({...form, stock_minimo: e.target.value})} />
            </Field>
            <Field label="Stock crítico">
              <input type="number" min="0" step="1" value={form.stock_critico} onChange={e => setForm({...form, stock_critico: e.target.value})} />
            </Field>
            <Field label="Proveedor">
              <select value={form.proveedor_id} onChange={e => setForm({...form, proveedor_id: e.target.value})}>
                <option value="">— sin proveedor —</option>
                {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Disponibilidad">
            <label style={{ fontSize: 14 }}>
              <input type="checkbox" checked={form.vendible_suelto} onChange={e => setForm({...form, vendible_suelto: e.target.checked})} style={{ width: 'auto', marginRight: 8 }} />
              Vender suelto en La Tienda (no solo en kits)
            </label>
          </Field>

          <Field label="Notas internas">
            <textarea value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} rows="2" placeholder="Ej: Sustituir por X si Boyerco no tiene stock" />
          </Field>

          {err && <div className="err-box">{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn primary" disabled={saving || reanalizando}>
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, children, required }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <b style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>
        {label} {required && <span style={{ color: 'var(--err)' }}>*</span>}
      </b>
      {children}
    </label>
  )
}
